import React, {useState} from 'react';
import {styles} from './textField_styles.web';
import {TextFieldProps} from './textField_types.web';
import InfoIcon from '../../Icons/icons/info';
import {useTheme} from '../../../../utils/theming/themeProvider';
import Tooltip from '../../Tooltips/Tooltip/Tooltip.web';
import {
  labelFont,
  extraDescFont,
  inputFont,
  inputBorder,
  inputColor,
  inputBackgroundColor,
  helperTextColor,
} from './textField_config';

const TextField = (props: React.PropsWithChildren<TextFieldProps>) => {
  // Props
  const {
    id,
    className,
    state,
    textFieldSize,
    label,
    value,
    autoComplete,
    extraDescription,
    helperText,
    placeholder,
    isLoading,
    SkeletonComponent,
    type = 'text',
    containerStyle,
    leadingIcon,
    trailingIcon,
    onChange,
    onKeyPress,
  } = props;

  // Styles
  const [isHovered, setIsHovered] = useState(false);
  const currentState =
    state === 'enable' || state === 'active'
      ? isHovered
        ? 'hover'
        : 'enable'
      : state;

  const {colors, fontStyles} = useTheme();

  const LeadingIconComponent = leadingIcon?.Icon;
  const TrailingIconComponent = trailingIcon?.Icon;

  return (
    <div
      id={id}
      className={className}
      style={{...styles.textFieldContainer, ...containerStyle}}>
      {label || extraDescription ? (
        <div style={styles.textFieldTopTextContainer}>
          {label ? (
            <div style={{...styles.textFieldLabelContainer}}>
              <label
                style={{
                  ...labelFont(textFieldSize, fontStyles),
                  lineHeight:
                    labelFont(textFieldSize, fontStyles)?.lineHeight + 'px',
                  color: colors.neutral700,
                  margin: 0,
                }}>
                {label?.text}
              </label>
              {label?.tooltip && (
                <Tooltip {...label?.tooltip}>
                  <InfoIcon size={'16'} color={colors.neutral700} />
                </Tooltip>
              )}
            </div>
          ) : (
            <div></div>
          )}
          {extraDescription ? (
            <span
              style={{
                ...extraDescFont(textFieldSize, fontStyles),
                lineHeight:
                  extraDescFont(textFieldSize, fontStyles)?.lineHeight + 'px',
                color: colors.neutral600,
                margin: 0,
              }}>
              {extraDescription}
            </span>
          ) : null}
        </div>
      ) : null}
      {!isLoading || !SkeletonComponent ? (
        <div style={{width: '100%', position: 'relative'}}>
          {LeadingIconComponent ? (
            <div
              style={styles.textFieldLeadingIcon}
              onClick={leadingIcon?.onClick}>
              <LeadingIconComponent
                //@ts-ignore
                size={leadingIcon.size || '24'}
                color={leadingIcon.color || colors.neutral1000}
                //@ts-ignore
                style={leadingIcon.style || 'outline'}
              />
            </div>
          ) : null}
          <input
            onMouseOver={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onFocus={() => setIsHovered(true)}
            onBlur={() => setIsHovered(false)}
            style={{
              ...styles.textFieldInput,
              ...inputFont(textFieldSize, fontStyles),
              color: colors[inputColor(currentState, value)],
              borderColor:
                colors[inputBorder[currentState] as keyof typeof colors],
              backgroundColor: colors[inputBackgroundColor(currentState)],
              lineHeight:
                inputFont(textFieldSize, fontStyles)?.lineHeight + 'px',
              outlineColor:
                state !== 'disable' ? colors.primary700 : 'transparent',
              outlineWidth:
                state !== 'disable' && state !== 'error' && state !== 'success'
                  ? 1
                  : 0,
              borderWidth: state !== 'disable' ? 1 : 0,
              cursor: state !== 'disable' ? 'auto' : 'not-allowed',
              pointerEvents: state === 'disable' ? 'none' : 'auto',
              paddingRight: trailingIcon ? 44 : 12,
              paddingLeft: leadingIcon ? 44 : 12,
            }}
            {...props}
          />
          {TrailingIconComponent ? (
            <div
              style={styles.textFieldTrailingIcon}
              onClick={trailingIcon?.onClick}>
              <TrailingIconComponent
                //@ts-ignore
                size={trailingIcon.size || '24'}
                color={trailingIcon.color || colors.neutral400}
                //@ts-ignore
                style={trailingIcon.style || 'outline'}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <SkeletonComponent />
      )}
      {helperText ? (
        <span
          style={{
            ...extraDescFont(textFieldSize, fontStyles),
            color: colors[helperTextColor(currentState)],
            margin: 0,
            lineHeight:
              extraDescFont(textFieldSize, fontStyles)?.lineHeight + 'px',
          }}>
          {helperText}
        </span>
      ) : null}
    </div>
  );
};

export default TextField;
